import { useEffect, useRef, useState } from 'react';

export type AbaChat = 'diario' | 'local' | 'combate' | 'global' | 'comercio' | 'guilda' | 'alianca';
type AbaContada = Exclude<AbaChat, 'diario'>;

const ZERO: Record<AbaContada, number> = {
  local: 0, combate: 0, global: 0, comercio: 0, guilda: 0, alianca: 0
};

interface Fatias {
  readonly local: readonly { readonly name: string }[];
  readonly combat: readonly unknown[];
  readonly global: readonly unknown[];
  readonly trade: readonly unknown[];
  readonly guild: readonly unknown[];
  readonly alliance: readonly unknown[];
}

/**
 * Conta fora do ChatGump: ele desmonta quando o chat vira linha ou é minimizado.
 * Conta quando a lista cresce e a aba não está visível. O Diário não conta.
 */
export function useChatNaoLidas(world: Fatias, visivel: boolean, eu = '') {
  const [aba, setAba] = useState<AbaChat>('diario');
  const [naoLidas, setNaoLidas] = useState(ZERO);
  const abaRef = useRef(aba);
  const visivelRef = useRef(visivel);
  abaRef.current = aba;
  visivelRef.current = visivel;

  useEffect(() => {
    if (!visivel || aba === 'diario') return;
    setNaoLidas(u => (u[aba] === 0 ? u : { ...u, [aba]: 0 }));
  }, [visivel, aba]);

  const prev = useRef({
    local: world.local.length,
    combate: world.combat.length,
    global: world.global.length,
    comercio: world.trade.length,
    guilda: world.guild.length,
    alianca: world.alliance.length
  });

  useEffect(() => {
    const pares: ReadonlyArray<[AbaContada, number]> = [
      ['local', world.local.length],
      ['combate', world.combat.length],
      ['global', world.global.length],
      ['comercio', world.trade.length],
      ['guilda', world.guild.length],
      ['alianca', world.alliance.length]
    ];
    const soma: Partial<Record<AbaContada, number>> = {};
    for (const [id, n] of pares) {
      const antes = prev.current[id];
      prev.current[id] = n;
      const mostra = visivelRef.current && abaRef.current === id;
      if (n <= antes || mostra) continue;
      // A própria fala não conta como não lida.
      const novas = id === 'local'
        ? world.local.slice(antes).filter(l => l.name !== eu).length
        : n - antes;
      if (novas > 0) soma[id] = novas;
    }
    if (Object.keys(soma).length === 0) return;
    setNaoLidas(u => {
      const next = { ...u };
      for (const id of Object.keys(soma) as AbaContada[]) next[id] += soma[id] ?? 0;
      return next;
    });
  }, [world.local, world.combat, world.global, world.trade, world.guild, world.alliance]);

  const total = naoLidas.local + naoLidas.combate + naoLidas.global + naoLidas.comercio + naoLidas.guilda + naoLidas.alianca;
  return { aba, setAba, naoLidas, total };
}
